'use client';

import { useTranslations } from 'next-intl';
import { ADDED_TO_BASKET_MODAL_ID, PRODUCT_INFO_QUICK_VIEW_MENU_ID } from '@headless-workspace/config';
import { unwrapResultSuccess } from '@headless-workspace/core-domain';
import { useProductDetailContext, useToasts, useUIContext } from '@headless-workspace/core-ui';
import { IconName } from '@headless-workspace/glow-ds';
import { FunctionHelpers } from '@headless-workspace/utils';
import { useBasketMutations, useProductDeliveryInfo } from '../../../../client';
import { AllStoresStockStatus, BasketProductValue, WarehouseStockStatus } from '../../../../server';

type UseProductBasketCTAsParams = {
    hasShortLabel: boolean;
};

export enum ActionStatus {
    AddToBasketCondition = 'ADD_TO_BASKET_CONDITION',
    ChooseStoreCondition = 'CHOOSE_STORE_CONDITION',
    UpdateStoreCondition = 'UPDATE_STORE_CONDITION',
    NotifyMeCondition = 'NOTIFY_ME_CONDITION',
}

type CTA = {
    iconName?: IconName;
    label: string;
    onClick?: () => void | Promise<BasketProductValue | undefined>;
    variant: 'primary' | 'secondary' | null;
    actionStatus?: ActionStatus;
};

type UseProductBasketCTAsReturnType = {
    isLoading: boolean;
    cta?: CTA;
    isAddingToBasket: boolean;
};

/**
 * This hook is used to manage the business rules for the product basket CTA.
 * It will check the product delivery information and return the appropriate CTA based on the product's availability.
 */
export const useProductBasketCTABusinessRules = ({
    hasShortLabel,
}: UseProductBasketCTAsParams): UseProductBasketCTAsReturnType => {
    const t = useTranslations('Discover.Pdp.information.basket');
    const {
        menuModal: { close: closeMenuModal, sideMenuModalList },
    } = useUIContext();
    const { notifySuccess, notifyError } = useToasts();

    // Basket management functions
    const { isMutating: isAddingToBasket, add: addToBasket } = useBasketMutations();

    // Retrieve product's delivery info
    const { sku } = useProductDetailContext();
    const { isLoading: isProductDeliveryInfoLoading, result: productDeliveryInfoResult } = useProductDeliveryInfo(sku);
    const productDeliveryInfo = unwrapResultSuccess(productDeliveryInfoResult);

    if (isProductDeliveryInfoLoading) {
        return { isLoading: isProductDeliveryInfoLoading, isAddingToBasket: false };
    }

    // When product delivery information is missing, we assume the product is out of stock.
    if (!productDeliveryInfo) {
        return {
            isLoading: isProductDeliveryInfoLoading,
            cta: {
                label: t('action.outOfStock'),
                variant: null,
            },
            isAddingToBasket: false,
        };
    }

    // Menu information
    const isQuickViewOpen = sideMenuModalList[PRODUCT_INFO_QUICK_VIEW_MENU_ID]?.isOpen ?? false;
    const isBasketModalOpen = sideMenuModalList[ADDED_TO_BASKET_MODAL_ID]?.isOpen ?? false;

    const handleAddToBasket = async (): Promise<BasketProductValue | undefined> => {
        const addToBasketResult = await addToBasket({ id: sku });

        if (addToBasketResult.type !== 'success') {
            notifyError(t('toast.label.error'));
            return undefined;
        }

        const customerBasket = unwrapResultSuccess(addToBasketResult);
        const product = customerBasket?.products.find((product) => product.productId === sku);

        const bothModalsOpen = isQuickViewOpen && isBasketModalOpen;

        if (isQuickViewOpen) {
            closeMenuModal(PRODUCT_INFO_QUICK_VIEW_MENU_ID, true);
        }

        if (bothModalsOpen || isBasketModalOpen) {
            closeMenuModal(ADDED_TO_BASKET_MODAL_ID, true);
            notifySuccess(t('toast.label.success'));
            return undefined;
        }

        return product;
    };

    const getCTA = (): CTA => {
        if (productDeliveryInfo.isAvailable) {
            const addLabel = hasShortLabel ? t('action.shortLabel.add') : t('action.add');

            if (productDeliveryInfo.warehouse?.stockStatus === WarehouseStockStatus.Available) {
                return {
                    label: addLabel,
                    onClick: handleAddToBasket,
                    variant: 'primary',
                    actionStatus: ActionStatus.AddToBasketCondition,
                };
            }

            if (productDeliveryInfo.allStores?.stockStatus === AllStoresStockStatus.Available) {
                if (!productDeliveryInfo.currentStore) {
                    return {
                        label: t('action.chooseStore'),
                        onClick: FunctionHelpers.emptyFn,
                        variant: 'primary',
                        actionStatus: ActionStatus.ChooseStoreCondition,
                    };
                }

                // FIXME: Include in this condition the case where the product is DTS eligible
                // FIXME: DTS eligibility function will be exposed by useDeliveryToStoreBusinessRules
                if (
                    productDeliveryInfo.currentStore.isAvailableInStore &&
                    productDeliveryInfo.currentStore.isClickAndCollectEnabled
                ) {
                    return {
                        label: addLabel,
                        onClick: handleAddToBasket,
                        variant: 'primary',
                        actionStatus: ActionStatus.AddToBasketCondition,
                    };
                }

                return {
                    label: t('action.changeStore'),
                    onClick: FunctionHelpers.emptyFn,
                    variant: 'primary',
                    actionStatus: ActionStatus.UpdateStoreCondition,
                };
            }
        }

        return productDeliveryInfo.isNotifyMeEnabled
            ? {
                  iconName: IconName.Notification,
                  label: t('action.outOfStockAndNotifyMe'),
                  onClick: FunctionHelpers.emptyFn,
                  variant: 'secondary',
                  actionStatus: ActionStatus.NotifyMeCondition,
              }
            : {
                  label: t('action.outOfStock'),
                  variant: null,
              };
    };

    return { isLoading: isProductDeliveryInfoLoading, cta: getCTA(), isAddingToBasket };
};
